import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { CancelSmallMinor, ProductReturnsMinor } from '@shopify/polaris-icons'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { ActionButton } from 'src/components/shared/ActionButton'
import tw from 'twin.macro'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { CircularProgress } from '@mui/material'
import { LinkSubcontractorTableContext } from '../LinkSubcontractorTable.context'
import { addS } from 'src/utils/baseUtils'
import { SensitiveActionWithPictureModal } from 'src/components/shared/SubmitActionWithPictureModal'
import NotFoundImage from 'src/assets/images/not-found.png'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { LinkSubcontractorContext } from '../../LinkSubcontractor.context'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  AssignContractorsToCustomer,
  UnassignContractorsFromCustomer,
} from 'src/constants/actionTypes'
import { useDispatch } from 'react-redux'
import { LinkedSubcontractor } from 'src/interfaces/subcontractor'
import { SubcontractorRequestStatus } from 'src/constants/subcontractorRequests/enum'

interface Props {
  contractors: LinkedSubcontractor[]
}

const LinkSubcontractorBatchActionButtonComponent = ({ contractors }: Props) => {
  const dispatch = useDispatch()

  const { selectedContractorIds } = useContext(LinkSubcontractorTableContext)
  const { reload } = useContext(ReloadWrapperContext)
  const { selectedMCCustomerId } = useContext(LinkSubcontractorContext)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const isOpen = Boolean(anchorEl)

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleRemoveSubmit = useCallback(async () => {
    if (!selectedContractorIds) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        UnassignContractorsFromCustomer.request({
          selectedContractorIds,
          customerId: selectedMCCustomerId,
        }),
      )

      showToast('Contractors removed.')

      reload()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
      handleCloseConfirm()
    }
  }, [selectedContractorIds, reload])

  const { canSubmitAgain, canRemove } = useMemo(() => {
    let canSubmitAgainResult = false
    let canRemoveResult = false

    if (!selectedContractorIds.length || !contractors?.length) {
      return {
        canSubmitAgain: canSubmitAgainResult,
        canRemove: canRemoveResult,
      }
    }

    const selectedContractors = contractors.filter((contractor) =>
      selectedContractorIds.includes(contractor.id),
    )

    canSubmitAgainResult = selectedContractors.every(
      (contractor) =>
        contractor.status === SubcontractorRequestStatus.Inactive ||
        contractor.status === SubcontractorRequestStatus.Rejected,
    )

    canRemoveResult = selectedContractors.every(
      (contractor) =>
        contractor.status === SubcontractorRequestStatus.Active ||
        contractor.status === SubcontractorRequestStatus.PendingCustomerReview,
    )

    return {
      canSubmitAgain: canSubmitAgainResult,
      canRemove: canRemoveResult,
    }
  }, [selectedContractorIds, contractors])

  const actionItems: MenuActionItem[] = useMemo(() => {
    const res: MenuActionItem[] = []

    if (canSubmitAgain) {
      res.push({
        label: 'Submit again',
        icon: isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <ProductReturnsMinor width={20} height={20} />
        ),
        onClick: async () => {
          if (!selectedContractorIds.length) {
            return
          }

          setIsLoading(true)

          try {
            await createAsyncAction(
              dispatch,
              AssignContractorsToCustomer.request({
                partnerDriverIds: selectedContractorIds,
                customerId: selectedMCCustomerId,
              }),
            )

            showToast('Contractor(s) submitted.')

            reload()
          } catch (err) {
            handleApiError(err)
          } finally {
            setIsLoading(false)
            handleClose()
          }
        },
      })
    }

    if (canRemove) {
      res.push({
        label: 'Remove',
        icon: isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <CancelSmallMinor width={20} height={20} />
        ),
        onClick: async () => {
          if (!selectedContractorIds.length) {
            return
          }

          handleOpenConfirm()
          handleClose()
        },
      })
    }

    return res
  }, [
    isLoading,
    selectedContractorIds,
    dispatch,
    selectedMCCustomerId,
    reload,
    canSubmitAgain,
    canRemove,
  ])

  return (
    <>
      <ActionButton
        className={tw`px-4`}
        selected={!!anchorEl}
        color="white"
        text="Actions"
        disabled={!selectedContractorIds.length || !actionItems.length}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        itemClassName={tw`w-[200px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      />
      <SensitiveActionWithPictureModal
        isOpen={isConfirmOpen}
        title={`Are you sure you want to remove ${addS(
          selectedContractorIds.length,
          'contractor',
        )}?`}
        text={`By removing the contractor, they will disappear from the customer's system. You can link the contractor again at any time.`}
        submitLabel="Remove"
        onClose={handleCloseConfirm}
        onSubmit={handleRemoveSubmit}
        imageSrc={NotFoundImage}
      />
    </>
  )
}

export const LinkSubcontractorBatchActionButton = memo(LinkSubcontractorBatchActionButtonComponent)
